/** Search acronyms (mockup v7): categories, live "finds N docs", admins add and remove. */
import { useMemo, useState } from "react";
import { api } from "../lib/api";
import { useStore } from "../lib/store";
import { ACR_CATS, coName } from "../lib/brand";
import { Co, Loading } from "../components/bits";
import { Icon } from "../components/Icon";
import { search as runSearch } from "../../../shared/search";
import { PartTag } from "./settings/SettingsLayout";

export function AcronymsPage() {
  const { p, terms, reloadTerms, allDocs, toast } = useStore();
  const [q, setQ] = useState("");
  const [cat, setCat] = useState("");
  const [term, setTerm] = useState("");
  const [exp, setExp] = useState("");
  const [newCat, setNewCat] = useState("Security");
  const [scope, setScope] = useState("CPY");
  const [busy, setBusy] = useState(false);

  const canAdd = (code: string) => p.anyAdmin && (code === "CPY" ? p.has("CPY", "admin") : p.adminCodes.includes(code));
  const pool = useMemo(() => (allDocs ?? []).map((d) => ({ ...d, full_number: d.number })), [allDocs]);
  const finds = (t: (typeof terms)[number]) => runSearch(t.term, pool.filter((d) => t.company === "CPY" || d.company === t.company), [t]).hits.length;

  const ql = q.trim().toLowerCase();
  const list = terms.filter((a) => (!cat || a.category === cat) && (!ql || a.term.toLowerCase().includes(ql) || a.expansion.toLowerCase().includes(ql)));

  const add = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!term.trim() || !exp.trim()) return toast("Enter a term and what it means.");
    setBusy(true);
    try {
      await api.addSearchTerm({ term: term.trim().toUpperCase(), expansion: exp.trim(), category: newCat, company: scope });
      toast(`Added ${term.trim().toUpperCase()} → ${exp.trim()}. Search uses it immediately.`);
      setTerm(""); setExp("");
      await reloadTerms();
    } catch (err) { toast((err as Error).message); }
    finally { setBusy(false); }
  };
  const remove = async (id: string, t: string) => {
    try { await api.removeSearchTerm(id); toast(`Removed ${t}.`); await reloadTerms(); }
    catch (err) { toast((err as Error).message); }
  };

  if (!allDocs) return <Loading />;
  return (
    <div className="view">
      <div className="hgroup"><div>
        <PartTag part="vault" extra={`${terms.length} terms`} />
        <h1 className="title">Search <em>acronyms</em></h1>
        <div className="lede">Search also matches every title's initials automatically, so "Incident Response Policy" is found by IR and IRP with no entry here. This list covers the IT, security and compliance shorthand people actually type.</div>
      </div></div>
      <div className="filters">
        <label className="inline-search"><Icon name="search" size={16} /><input placeholder="Find a term" value={q} onChange={(e) => setQ(e.target.value)} aria-label="Find a term" /></label>
        <div className="chips">
          <button className={`chip ${!cat ? "on" : ""}`} onClick={() => setCat("")}>All</button>
          {Object.entries(ACR_CATS).map(([c, col]) => (
            <button key={c} className={`chip ${cat === c ? "on" : ""}`} onClick={() => setCat(c)} style={{ display: "inline-flex", gap: 6, alignItems: "center" }}>
              <i style={{ width: 8, height: 8, borderRadius: "50%", background: col, display: "inline-block" }} />{c} <span style={{ opacity: 0.6 }}>{terms.filter((a) => a.category === c).length}</span>
            </button>
          ))}
        </div>
      </div>
      <div className="card tbl-wrap">
        <table className="tbl dt">
          <colgroup><col style={{ width: 110 }} /><col /><col style={{ width: 140 }} /><col className="hide-md" style={{ width: 170 }} /><col style={{ width: 90 }} />{p.anyAdmin && <col style={{ width: 96 }} />}</colgroup>
          <thead><tr><th>Term</th><th>Means</th><th>Category</th><th className="hide-md">Applies to</th><th>Finds</th>{p.anyAdmin && <th />}</tr></thead>
          <tbody>
            {list.length ? list.map((a) => {
              const n = finds(a);
              return (
                <tr key={a.id} style={{ cursor: "default" }}>
                  <td className="docno" style={{ fontSize: 13, fontWeight: 700 }}>{a.term}</td>
                  <td style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{a.expansion}</td>
                  <td><span className="cat" style={{ ["--cc" as string]: ACR_CATS[a.category] ?? "#3E4651" }}>{a.category || "Custom"}</span></td>
                  <td className="hide-md"><Co code={a.company} label={a.company === "CPY" ? "All companies" : coName(a.company)} /></td>
                  <td className="due" style={n ? { color: "var(--ok)", fontWeight: 700 } : { color: "var(--faint)" }}>{n} doc{n === 1 ? "" : "s"}</td>
                  {p.anyAdmin && <td style={{ textAlign: "right" }}>{canAdd(a.company) && <button className="btn btn-ghost btn-sm" onClick={() => void remove(a.id, a.term)}>Remove</button>}</td>}
                </tr>
              );
            }) : <tr><td colSpan={6} className="empty">No terms match.</td></tr>}
          </tbody>
        </table>
      </div>
      {p.anyAdmin ? (
        <form className="card form" onSubmit={add} style={{ marginTop: 14, gridTemplateColumns: "120px 1fr 160px 200px auto", alignItems: "end", display: "grid" }}>
          <label><span className="lbl">Term</span><input className="inp" value={term} onChange={(e) => setTerm(e.target.value)} placeholder="e.g., ZTA" maxLength={20} /></label>
          <label><span className="lbl">Means</span><input className="inp" value={exp} onChange={(e) => setExp(e.target.value)} placeholder="e.g., Zero Trust Architecture" maxLength={120} /></label>
          <label><span className="lbl">Category</span><select className="inp" value={newCat} onChange={(e) => setNewCat(e.target.value)}>{Object.keys(ACR_CATS).map((c) => <option key={c}>{c}</option>)}</select></label>
          <label><span className="lbl">Applies to</span>
            <select className="inp" value={scope} onChange={(e) => setScope(e.target.value)}>
              {p.has("CPY", "admin") && <option value="CPY">All companies</option>}
              {p.adminCodes.map((c) => <option key={c} value={c}>{coName(c)}</option>)}
            </select></label>
          <button className={`btn btn-accent ${busy ? "busy" : ""}`} type="submit">Add</button>
        </form>
      ) : <div className="foot-note">Ask an admin to add acronyms.</div>}
    </div>
  );
}
